import { useEffect, useMemo, useState } from 'react';

import { Box, makeStyles, TextField, Typography } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';

import useCurrentUser from 'hooks/useCurrentUser';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchSelectAtendentes,
  UsuarioInterfaceV2,
  usuariosSelector,
} from 'store/reducers/usuarios';

import { ContactAvatar } from './ContactAvatar';

const useStyles = makeStyles(() => ({
  truncateValue: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  truncateText: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  avatar: {
    width: 24,
    height: 24,
  },
}));

interface AtendenteSelectFieldProps {
  atendenteId?: string;
  onFilter: (atendente: UsuarioInterfaceV2 | null) => void;
  onReset: () => void;
  label?: string;
  hideCurrentUser?: boolean;
}

export default function AtendenteSelectField({
  atendenteId,
  onFilter,
  onReset,
  label = 'Atendente',
  hideCurrentUser = false,
}: AtendenteSelectFieldProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [atendenteSelecionado, setAtendenteSelecionado] =
    useState<UsuarioInterfaceV2 | null>(null);

  const [open, setOpen] = useState(false);

  const currentUser = useCurrentUser();
  const { selectAtendentes, loadingSelectAtendentes } =
    useAppSelector(usuariosSelector);

  const filteredOptions = useMemo(() => {
    if (hideCurrentUser && currentUser) {
      return selectAtendentes.filter(
        atendente => atendente.id !== currentUser.id,
      );
    }
    return selectAtendentes;
  }, [hideCurrentUser, currentUser, selectAtendentes]);

  useEffect(() => {
    dispatch(fetchSelectAtendentes());
  }, [dispatch]);

  useEffect(() => {
    if (atendenteId) {
      const atendente = selectAtendentes.find(
        atendente => atendente.id === atendenteId,
      );
      setAtendenteSelecionado(atendente || null);
    } else {
      setAtendenteSelecionado(null);
    }
  }, [atendenteId, selectAtendentes]);

  const handleChange = (
    _event: React.ChangeEvent<Record<string, unknown>>,
    value: UsuarioInterfaceV2 | null,
  ) => {
    setAtendenteSelecionado(value);

    if (value) {
      onFilter(value);
    } else {
      onFilter(null);
      onReset();
    }
  };

  return (
    <Autocomplete
      id="atendente-select-field"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={atendenteSelecionado}
      options={filteredOptions}
      loading={loadingSelectAtendentes}
      autoHighlight
      size="small"
      onChange={handleChange}
      popupIcon={<ExpandMore style={{ fontSize: 18 }} />}
      closeText="Fechar"
      openText="Abrir"
      clearText="Limpar"
      getOptionLabel={option => option?.name || ''}
      getOptionSelected={(option, value) => option.id === value.id}
      noOptionsText={
        <Typography variant="body2">Nenhum atendente encontrado</Typography>
      }
      renderOption={atendente => (
        <Box className={classes.truncateValue}>
          <ContactAvatar src={atendente.foto} className={classes.avatar} />
          <span className={classes.truncateText}>{atendente.name}</span>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          placeholder="Buscar atendente..."
          InputProps={{
            ...params.InputProps,
            startAdornment: atendenteSelecionado ? (
              <>
                <ContactAvatar
                  src={atendenteSelecionado.foto}
                  className={classes.avatar}
                />
                {params.InputProps.startAdornment}
                <Box mr={0.5} />
              </>
            ) : (
              params.InputProps.startAdornment
            ),
            endAdornment: params.InputProps.endAdornment,
          }}
        />
      )}
    />
  );
}
